import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { FunnelSimple, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

export default function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, brands: [], facets: [] });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const brand = params.get("brand") || "";
  const skus = params.get("skus") || "";
  const vehicle = params.get("vehicle") || "";
  const volume = params.get("volume") || "";
  const attrsParam = params.get("attrs") || "";
  const sort = params.get("sort") || "name";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: { category: category || undefined, q: q || undefined, brand: brand || undefined, skus: skus || undefined, volume: volume || undefined, attrs: attrsParam || undefined, sort, page, limit: 24 } })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, q, brand, skus, volume, attrsParam, sort, page]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next);
  };

  const toggleBrand = (b) => {
    const list = brand ? brand.split(",") : [];
    const next = list.includes(b) ? list.filter((x) => x !== b) : [...list, b];
    setParam("brand", next.join(","));
  };

  const selectedBrands = brand ? brand.split(",") : [];
  const selectedVolumes = volume ? volume.split(",") : [];
  const selectedAttrs = attrsParam ? attrsParam.split("||") : [];
  const hasActiveFilters = selectedBrands.length || selectedVolumes.length || selectedAttrs.length;

  const toggleVolume = (v) => {
    const next = selectedVolumes.includes(v) ? selectedVolumes.filter((x) => x !== v) : [...selectedVolumes, v];
    setParam("volume", next.join(","));
  };

  const toggleAttr = (key, value) => {
    const token = `${key}:${value}`;
    const next = selectedAttrs.includes(token) ? selectedAttrs.filter((x) => x !== token) : [...selectedAttrs, token];
    setParam("attrs", next.join("||"));
  };

  const clearFilters = () => {
    const next = new URLSearchParams(params);
    ["brand", "volume", "attrs", "page"].forEach((k) => next.delete(k));
    setParams(next);
  };

  const catName = categories.find((c) => c.id === category)?.name;
  const totalPages = Math.ceil(data.total / 24);

  const Filters = () => (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-3">Kategorija</p>
        <ul className="space-y-1">
          <li>
            <button data-testid="filter-cat-all" onClick={() => setParam("category", "")}
              className={`text-sm hover:text-[#2E86D1] ${!category ? "font-bold text-[#2E86D1]" : ""}`}>Visas kategorijas</button>
          </li>
          {categories.map((c) => (
            <li key={c.id}>
              <button data-testid={`filter-cat-${c.id}`} onClick={() => setParam("category", c.id)}
                className={`text-left text-sm hover:text-[#2E86D1] ${category === c.id ? "font-bold text-[#2E86D1]" : ""}`}>
                {c.name} <span className="text-slate-400">({c.count})</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      {data.brands.length > 1 && (
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-3">Zīmols</p>
          <ul className="space-y-2">
            {data.brands.map((b) => (
              <li key={b} className="flex items-center gap-2">
                <input type="checkbox" id={`brand-${b}`} data-testid={`filter-brand-${b}`} checked={selectedBrands.includes(b)}
                  onChange={() => toggleBrand(b)} className="h-4 w-4 accent-[#2E86D1]" />
                <label htmlFor={`brand-${b}`} className="text-sm cursor-pointer">{b}</label>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(data.facets || []).map((f) => (
        <div key={f.key}>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-3">{f.key}</p>
          <ul className="space-y-2">
            {f.values.map(({ value, count }) => {
              const checked = f.param === "volume" ? selectedVolumes.includes(value) : selectedAttrs.includes(`${f.key}:${value}`);
              const id = `facet-${f.key}-${value}`.replace(/\s+/g, "-");
              return (
                <li key={value} className="flex items-center gap-2">
                  <input type="checkbox" id={id} data-testid={id} checked={checked}
                    onChange={() => (f.param === "volume" ? toggleVolume(value) : toggleAttr(f.key, value))}
                    className="h-4 w-4 accent-[#2E86D1]" />
                  <label htmlFor={id} className="text-sm cursor-pointer">{value} <span className="text-slate-400">({count})</span></label>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {hasActiveFilters ? (
        <button data-testid="clear-filters-btn" onClick={clearFilters}
          className="btn-industrial w-full border border-slate-300 px-4 py-2 text-xs font-semibold hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]">
          ✕ Notīrīt filtrus
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <nav className="text-xs text-slate-500"><Link to="/" className="hover:text-[#2E86D1]">Sākums</Link> / <span>Katalogs</span>{catName && <span> / {catName}</span>}</nav>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
          {vehicle ? `Detaļas: ${vehicle}` : catName || (q ? `Meklēšana: “${q}”` : "Viss katalogs")}
        </h1>
        <div className="flex items-center gap-3">
          <button data-testid="mobile-filters-btn" onClick={() => setFiltersOpen(!filtersOpen)} className="lg:hidden flex items-center gap-1.5 border border-slate-300 px-3 py-2 text-xs font-semibold">
            {filtersOpen ? <X size={16} /> : <FunnelSimple size={16} />} Filtri
          </button>
          <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
            className="border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="name">Pēc nosaukuma</option>
            <option value="price_asc">Cena: augoša</option>
            <option value="price_desc">Cena: dilstoša</option>
          </select>
        </div>
      </div>
      <p data-testid="results-count" className="mt-2 text-sm text-slate-500">{data.total} produkti
        {vehicle && <button data-testid="clear-vehicle-btn" onClick={() => setParams({})} className="ml-3 text-xs font-semibold text-[#2E86D1] hover:underline">✕ Notīrīt auto filtru</button>}
      </p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <aside className={`${filtersOpen ? "block" : "hidden"} lg:block`}>
          <Filters />
        </aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-[#F4F5F7] animate-pulse" />)}
            </div>
          ) : data.items.length === 0 ? (
            <p data-testid="no-results" className="py-16 text-center text-slate-500">Nekas netika atrasts. Mēģiniet citu meklēšanas frāzi.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {data.items.map((p) => <ProductCard key={p.sku} product={p} />)}
            </div>
          )}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button key={i} data-testid={`page-btn-${i + 1}`} onClick={() => setParam("page", String(i + 1))}
                  className={`btn-industrial h-9 w-9 border text-sm font-bold ${page === i + 1 ? "bg-[#1D1D1F] text-white border-slate-300" : "border-border hover:border-[#2E86D1]"}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
